import React, { useState, useEffect, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import { toast } from "react-toastify";
import "react-big-calendar/lib/css/react-big-calendar.css";
import ptBR from "date-fns/locale/pt-BR";

const locales = { "pt-BR": ptBR };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const messages = {
  date: "Data",
  time: "Hora",
  event: "Evento",
  allDay: "Dia inteiro",
  week: "Semana",
  work_week: "Semana de trabalho",
  day: "Dia",
  month: "Mês",
  previous: "Anterior",
  next: "Próximo",
  today: "Hoje",
  agenda: "Agenda",
  noEventsInRange: "Nenhum evento neste intervalo",
  showMore: (total) => `+${total} mais`,
};

const HORARIOS = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
];

function CalendarioAgenda({ idMedico }) {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [disponibilidades, setDisponibilidades] = useState({}); // Available times
  const [consultas, setConsultas] = useState({}); // Occupied times with consultation details
  const [horasSelecionadas, setHorasSelecionadas] = useState([]);

  const fetchDisponibilidades = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/agendaMedicoDashboard?idMedico=${idMedico}`
      );
      if (!response.ok) {
        throw new Error("Erro ao buscar agenda.");
      }
      const data = await response.json();

      // Mapear disponibilidades (free slots) e consultas (occupied slots) por dia
      const mapDisponibilidades = {};
      const mapConsultas = {};
      data.forEach((item) => {
        // const dataUTC = new Date(item.data_inicio);
        const dataUTC = new Date(item.data_inicio.replace("Z", ""));
        console.log("Data UTC:", dataUTC);

        // const dataLocal = new Date(
        //   dataUTC.getTime() + dataUTC.getTimezoneOffset() * 60000
        // );

        console.log("Data UTC:", dataUTC);

        const dia = format(dataUTC, "yyyy-MM-dd");

        const horaInicio = item.hora_inicio.slice(0, 5); // e.g., "18:00:00" -> "18:00"

        if (!mapDisponibilidades[dia]) {
          mapDisponibilidades[dia] = [];
          mapConsultas[dia] = [];
        }

        if (item.ocupado) {
          mapConsultas[dia].push({ hora: horaInicio, consulta: item.consulta });
        } else {
          if (!mapDisponibilidades[dia].includes(horaInicio)) {
            mapDisponibilidades[dia].push(horaInicio);
          }
        }
      });
      setDisponibilidades(mapDisponibilidades);
      setConsultas(mapConsultas);

      // Criar eventos para o calendário
      const newEvents = [];
      data.forEach((item) => {
        const dia = format(
          new Date(item.data_inicio.replace("Z", "")),
          "yyyy-MM-dd"
        );
        const [year, month, day] = dia.split("-");
        const [hour, minute] = item.hora_inicio.split(":");
        const startDate = new Date(year, month - 1, day, hour, minute);
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        newEvents.push({
          title: item.ocupado
            ? `Consulta (${item.consulta.status})`
            : `Disponível: ${item.hora_inicio.slice(0, 5)}`,
          start: startDate,
          end: endDate,
          isOccupied: item.ocupado,
          consulta: item.ocupado ? item.consulta : null,
        });
      });
      setEvents(newEvents);
    } catch (error) {
      toast.error(error.message);
    }
  }, [idMedico]);

  useEffect(() => {
    fetchDisponibilidades();
  }, [fetchDisponibilidades]);

  const handleSelectSlot = (slotInfo) => {
    const selected = new Date(slotInfo.start);
    setSelectedDate(selected);
    setModalOpen(true);
  };

  useEffect(() => {
    if (selectedDate && modalOpen) {
      const diaStr = format(selectedDate, "yyyy-MM-dd");
      const existingTimes = disponibilidades[diaStr] || [];
      setHorasSelecionadas([...existingTimes]);
    } else {
      setHorasSelecionadas([]);
    }
  }, [selectedDate, modalOpen, disponibilidades]);

  const toggleHora = (hora) => {
    setHorasSelecionadas((prevHoras) =>
      prevHoras.includes(hora)
        ? prevHoras.filter((h) => h !== hora)
        : [...prevHoras, hora]
    );
  };

  const incrementarUmaHora = (hora) => {
    const [h, m] = hora.split(":");
    const newHour = parseInt(h, 10) + 1;
    return `${newHour < 10 ? `0${newHour}` : newHour}:${m}`;
  };

  const handleSalvar = async () => {
    if (!selectedDate) return;

    const diaStr = format(selectedDate, "yyyy-MM-dd");

    const scheduleToSave = horasSelecionadas.map((hora) => ({
      data_inicio: diaStr,
      hora_inicio: hora,
      hora_fim: incrementarUmaHora(hora),
      idMedico,
    }));

    const payload =
      scheduleToSave.length === 0
        ? { schedule: [], idMedico, data: diaStr } // Se estiver limpando
        : { schedule: scheduleToSave };

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/agenda`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Falha ao salvar agenda.");
      }

      toast.success("Agenda atualizada com sucesso!");
      setModalOpen(false);
      await fetchDisponibilidades();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getButtonColor = (hora, diaStr) => {
    const consultasDoDia = consultas[diaStr] || [];
    const consulta = consultasDoDia.find((c) => c.hora === hora);
    if (consulta) {
      return "danger"; // Red for occupied slots
    }
    return horasSelecionadas.includes(hora) ? "primary" : "secondary";
  };

  const getButtonDisabled = (hora, diaStr) => {
    const consultasDoDia = consultas[diaStr] || [];
    return !!consultasDoDia.find((c) => c.hora === hora); // Disable if occupied
  };

  return (
    <div>
      <h3>Agenda do Médico (ID: {idMedico})</h3>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 500 }}
        selectable
        onSelectSlot={handleSelectSlot}
        messages={messages}
        culture="pt-BR"
        defaultView="month"
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.isOccupied ? "#dc3545" : "#007bff", // Red for occupied, blue for available
            color: "white",
          },
        })}
      />

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Agenda para {selectedDate ? format(selectedDate, "dd/MM/yyyy") : null}
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {HORARIOS.map((hora) => {
              const diaStr = selectedDate
                ? format(selectedDate, "yyyy-MM-dd")
                : "";
              const consultasDoDia = consultas[diaStr] || [];
              const consulta = consultasDoDia.find((c) => c.hora === hora);
              const buttonColor = getButtonColor(hora, diaStr);
              const isDisabled = getButtonDisabled(hora, diaStr);

              return (
                <div key={hora} style={{ margin: "5px" }}>
                  <Button
                    color={buttonColor}
                    onClick={() => !isDisabled && toggleHora(hora)}
                    disabled={isDisabled}
                    style={{ minWidth: "120px" }}
                  >
                    {hora} - {incrementarUmaHora(hora)}
                  </Button>
                  {consulta && (
                    <div style={{ fontSize: "0.8rem", marginTop: "5px" }}>
                      <strong>Consulta:</strong> {consulta.consulta.status}
                      <br />
                      Cliente ID: {consulta.consulta.id_cliente}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSalvar}>
            Salvar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default CalendarioAgenda;
